// providers/ImportStatus/importStatus.provider.jsx

import { createContext, useEffect, useReducer, useMemo, useRef } from 'react';
import { INITIAL_STATE, getWarningFromResult, isCodeChangeWarning } from './importStatus.constants';
import { useImportNavigation } from './useImportNavigation';
import ChangeCodeExploitationModal from 'components/pages/Exploitation/modal/ChangeCodeExploitationModal';
import { importActions, importReducer } from './ImportStatus.reducer';
import { toast } from 'react-toastify';
import i18next from '../../i18n';

const ImportContext = createContext();
const STORAGE_KEY = 'importContext';

function ImportProvider({ children }) {
  const isInitialLoadRef = useRef(true);

  // Initialize state from localStorage if available
  const [state, dispatch] = useReducer(importReducer, INITIAL_STATE, (initial) => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        return { ...initial, ...JSON.parse(saved) };
      }
      return initial;
    } catch {
      return initial;
    }
  });

  // Save to localStorage on every state change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const resetImportContext = () => {
    localStorage.removeItem(STORAGE_KEY);
    dispatch(importActions.reset());
  };

  const { navigateAfterImport } = useImportNavigation(resetImportContext);

  // Derived: warning data for modal
  const warningDataFromImport = useMemo(() => {
    return getWarningFromResult(state.result);
  }, [state.result]);

  // Effect: navigation after receiving result
  useEffect(() => {
    // Don't navigate on initial load (reload)
    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      return;
    }

    if (!state.result || !state.importFrom) {
      return;
    }

    // Check that config is complete
    const requiredFields = ['importFrom', 'importType', 'importOriginLocation'];
    const missingFields = requiredFields.filter((field) => !state[field]);

    // shouldn't trigger in prod environment but will be useful for dev
    if (missingFields.length > 0) {
      toast.error(
        i18next.t('errors.incompleteImportConfig', { missingFields: missingFields.join(', ') }),
      );
      return;
    }

    // If it's a code change, open modal (handled by reducer in SET_RESULT)
    // Otherwise navigate directly
    if (isCodeChangeWarning(state.result)) {
      return; // Modal will open, don't navigate
    } else {
      navigateAfterImport({
        result: state.result,
        jobId: state.jobId,
        importFrom: state.importFrom,
        importType: state.importType,
        importOriginLocation: state.importOriginLocation,
        nextCampaignChosen: state.nextCampaignChosen,
        geofoliaImportSteps: state.geofoliaImportSteps,
      });
    }
  }, [state.result, state.jobId]);

  // Actions exposed by context
  const actions = useMemo(() => ({
    setStatus: (status) => dispatch(importActions.setStatus(status)),
    setJobId: (jobId) => dispatch(importActions.setJobId(jobId)),
    setResult: (result, jobId) => dispatch(importActions.setResult(result, jobId)),
    setImportConfig: (config) => dispatch(importActions.setImportConfig(config)),
    setImportType: (importType) => dispatch(importActions.setImportConfig({ importType })),
    setImportFrom: (importFrom) => dispatch(importActions.setImportConfig({ importFrom })),
    setImportOriginLocation: (location) =>
      dispatch(importActions.setImportConfig({ importOriginLocation: location })),
    setNextCampaignChosen: (campaign) =>
      dispatch(importActions.setImportConfig({ nextCampaignChosen: campaign })),
    setGeofoliaImportSteps: (step) => dispatch(importActions.setGeofoliaStep(step)),
    setIsOpenChangeCodeModal: (isOpen) => {
      if (isOpen) {
        dispatch(importActions.openCodeChangeModal());
      } else {
        dispatch(importActions.closeCodeChangeModal());
      }
    },
    setMessageToDisplayInResult: (message) => dispatch(importActions.setMessageToDisplay(message)),
    resetImportContext: resetImportContext,
  }));

  // Navigation function after modal close
  const handleNavigateAfterCodeChange = () => {
    navigateAfterImport({
      result: state.result,
      jobId: state.jobId,
      importFrom: state.importFrom,
      importType: state.importType,
      importOriginLocation: state.importOriginLocation,
      nextCampaignChosen: state.nextCampaignChosen,
      geofoliaImportSteps: state.geofoliaImportSteps,
    });
  };

  // Value exposed by context
  const contextValue = useMemo(
    () => ({
      // State
      status: state.status,
      jobId: state.jobId,
      result: state.result,
      importFrom: state.importFrom,
      importType: state.importType,
      importOriginLocation: state.importOriginLocation,
      nextCampaignChosen: state.nextCampaignChosen,
      geofoliaImportSteps: state.geofoliaImportSteps,
      isOpenChangeCodeModal: state.isOpenChangeCodeModal,
      messageToDisplayInResult: state.messageToDisplayInResult,
      warningDataFromImport,

      // Actions
      ...actions,
    }),
    [state, warningDataFromImport, actions],
  );

  return (
    <ImportContext.Provider value={contextValue}>
      {children}
      {state.isOpenChangeCodeModal && (
        <ChangeCodeExploitationModal navigateAfterImport={handleNavigateAfterCodeChange} />
      )}
    </ImportContext.Provider>
  );
}

export { ImportContext, ImportProvider };
